import { clsx } from 'clsx';
import { useCallback, useEffect } from 'react';
import { useUIStore, useInventoryFilterStore, useInventoryColumnStore } from '@/stores';
import {
  FunnelIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import {
  LocalInventoryFilters,
  SLBPosition,
  InventoryState,
  NumericFilterCondition,
  DEFAULT_NUMERIC_CONDITION,
  DEFAULT_FILTER_DISPLAY_OPTION,
} from '@/types';
import { ColumnManagerPanel } from './ColumnManagerPanel';
import { CollapsibleFilterSection } from './CollapsibleFilterSection';
import { exportToCSV } from '@/utils/inventory/csv-export';
import { getDefaultStateId } from '@/utils/inventory/intraday-state-utils';
import { countActiveInventoryFilters } from '@/utils/inventory/count-active-filters';
import { FILTER_ID_TO_COLUMN_ID, getHasActiveValuesForFilter as getHasActiveValuesForFilterUtil } from '@/utils/inventory/filter-column-mapping';
import { sortFiltersByColumnOrder as sortFilters } from '@/utils/filter-utils';
import { ConditionFilter, MultiSelectFilter, StatementFilter } from './filters';
import { DEFAULT_FILTER_ITEMS, type FilterItem } from '@/types';
import type { ColumnConfig } from '@/stores/inventory/inventory-column-store';
import { SavedViewSelector } from '@/features/saved-views';
import {
  inventorySavedViewsController,
  getInventorySavedViewsHooks,
} from '@/features/inventory/saved-views';

const sortFiltersByColumnOrder = (filters: FilterItem[], columns: ColumnConfig[]) =>
  sortFilters(filters, columns, FILTER_ID_TO_COLUMN_ID, { first: 'statement', last: 'export' });

type SidebarTab = 'filters' | 'columns';

interface FilterSidebarProps {
  currentUserEmail?: string | null;
  onColumnsTabActiveChange?: (active: boolean) => void;
  onActiveViewChange?: (viewId: number | null) => void;
  accountIds?: string[];
  counterparties?: string[];
  currencies?: string[];
  caStatuses?: string[];
  quantityRange?: { min: number; max: number };
  quantityT1Range?: { min: number; max: number };
  quantityT2Range?: { min: number; max: number };
  quantityTxRange?: { min: number; max: number };
  leverageRateRange?: { min: number; max: number };
  valueEURRange?: { min: number; max: number };
  currencyBenchmarkRange?: { min: number; max: number };
  dailyPotentialRange?: { min: number; max: number };
  adtv30DRange?: { min: number; max: number };
  adtvMultipleRange?: { min: number; max: number };
  adtvAdjustedRange?: { min: number; max: number };
  benchmarkFeeRange?: { min: number; max: number };
  activeUtilisationRange?: { min: number; max: number };
  quantityOnLoanRange?: { min: number; max: number };
  rebateRateRange?: { min: number; max: number };
  filteredData?: SLBPosition[];
  currentStateName?: string;
  statesData?: InventoryState[];
  isStatesLoading?: boolean;
  isDataLoading?: boolean;
  hasError?: boolean;
}

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
  currentUserEmail,
  onColumnsTabActiveChange,
  onActiveViewChange,
  accountIds = [],
  counterparties = [],
  currencies = [],
  caStatuses = [],
  quantityRange = { min: 0, max: 1000000 },
  quantityT1Range = { min: 0, max: 1000000 },
  quantityT2Range = { min: 0, max: 1000000 },
  quantityTxRange = { min: 0, max: 1000000 },
  leverageRateRange = { min: 0, max: 100 },
  valueEURRange = { min: 0, max: 1000000 },
  currencyBenchmarkRange = { min: 0, max: 100 },
  dailyPotentialRange = { min: 0, max: 10000 },
  adtv30DRange = { min: 0, max: 1000000 },
  adtvMultipleRange = { min: 0, max: 100 },
  adtvAdjustedRange = { min: 0, max: 10000 },
  benchmarkFeeRange = { min: 0, max: 100 },
  activeUtilisationRange = { min: 0, max: 100 },
  quantityOnLoanRange = { min: 0, max: 1000000 },
  rebateRateRange = { min: -100, max: 100 },
  filteredData = [],
  currentStateName = '',
  statesData = [],
  isStatesLoading = false,
  isDataLoading = false,
  hasError = false,
}) => {
  const activeTab = useUIStore((s) => s.sidebarActiveTab) as SidebarTab;
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((state) => state.setColumnsTabActive);
  const handleTabChange = useCallback((tab: SidebarTab) => {
    setSidebarActiveTab(tab);
    setColumnsTabActive(tab === 'columns');
    onColumnsTabActiveChange?.(tab === 'columns');
  }, [onColumnsTabActiveChange, setColumnsTabActive, setSidebarActiveTab]);
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const presetIsDirty = inventorySavedViewsController.useStore((s) => s.isDirty);
  const toggleFilterSidebar = useUIStore((state) => state.toggleFilterSidebar);
  const expandedFilterIds = useUIStore((state) => state.expandedFilterIds);
  const focusedFilterId = useUIStore((state) => state.focusedFilterId);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const collapseFilter = useUIStore((state) => state.collapseFilter);
  const expandAllFilters = useUIStore((state) => state.expandAllFilters);
  const collapseAllFilters = useUIStore((state) => state.collapseAllFilters);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);
  const localInventoryFilters = useInventoryFilterStore((state) => state.localInventoryFilters);
  const setLocalInventoryFilters = useInventoryFilterStore((state) => state.setLocalInventoryFilters);
  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const setInventoryFilters = useInventoryFilterStore((state) => state.setInventoryFilters);
  const filterMetadata = useInventoryFilterStore((state) => state.filterMetadata);
  const filterDisplayOptions = useInventoryFilterStore((state) => state.filterDisplayOptions);
  const updateFilterDisplayOption = useInventoryFilterStore((state) => state.updateFilterDisplayOption);
  const columns = useInventoryColumnStore((state) => state.columns);
  

  const updateFilter = useCallback((key: keyof LocalInventoryFilters, value: LocalInventoryFilters[keyof LocalInventoryFilters]) => {
    setLocalInventoryFilters({
      ...localInventoryFilters,
      [key]: value,
    });
  }, [localInventoryFilters, setLocalInventoryFilters]);
  const clearAllFilters = useInventoryFilterStore((state) => state.clearLocalInventoryFilters);

  const getHasActiveValuesForFilter = useCallback(
    (filterId: string, _filterType: string): boolean =>
      getHasActiveValuesForFilterUtil(localInventoryFilters, filterId),
    [localInventoryFilters]
  );

  useEffect(() => {
    if (focusedFilterId == null) return;
    const t = setTimeout(() => setFocusedFilterId(null), 2000);
    return () => clearTimeout(t);
  }, [focusedFilterId, setFocusedFilterId]);

  const renderConditionFilter = useCallback(
    (
      filterId: string,
      label: string,
      conditionKey: keyof LocalInventoryFilters,
      valueRange: { min: number; max: number },
      step: number,
      hideLabelOption: boolean = true
    ) => {
      const condition = localInventoryFilters[conditionKey] as NumericFilterCondition;
      const opts = filterDisplayOptions[filterId] ?? DEFAULT_FILTER_DISPLAY_OPTION;
      return (
        <ConditionFilter
          label={label}
          filterId={filterId}
          condition={condition ?? DEFAULT_NUMERIC_CONDITION}
          onChange={(c) => updateFilter(conditionKey, c)}
          onReset={() => updateFilter(conditionKey, { ...DEFAULT_NUMERIC_CONDITION })}
          valueRange={valueRange}
          step={step}
          hideLabel={hideLabelOption}
          hideZero={opts.hideZero}
          hideBlanks={opts.hideBlanks}
          onHideZeroChange={(v) => updateFilterDisplayOption(filterId, 'hideZero', v)}
          onHideBlanksChange={(v) => updateFilterDisplayOption(filterId, 'hideBlanks', v)}
        />
      );
    },
    [
      localInventoryFilters,
      filterDisplayOptions,
      updateFilter,
      updateFilterDisplayOption,
    ]
  );

  const handleExport = useCallback((): void => {
    exportToCSV(filteredData, columns, currentStateName);
  }, [filteredData, columns, currentStateName]);


  // Helper function to render individual filter components
  const renderFilterComponent = useCallback((filterType: string) => {
    switch (filterType) {
      case 'statement':
        return (
          <StatementFilter
            value={inventoryFilters.state || getDefaultStateId(statesData || []) || 0}
            states={statesData || []}
            onChange={(stateId) => {
              setInventoryFilters({
                ...inventoryFilters,
                state: stateId,
              });
            }}
            disabled={isStatesLoading || isDataLoading || hasError}
          />
        );

      case 'accountId':
        return (
          <MultiSelectFilter
            label="Account ID"
            options={accountIds}
            selectedValues={localInventoryFilters.accountIds}
            onChange={(values) => updateFilter('accountIds', values)}
            onReset={() => updateFilter('accountIds', [])}
            placeholder="Select accounts..."
            searchPlaceholder="Search accounts..."
            hideLabel
          />
        );

      case 'counterparty':
        return (
          <MultiSelectFilter
            label="Counterparty"
            options={counterparties}
            selectedValues={localInventoryFilters.counterparties}
            onChange={(values) => updateFilter('counterparties', values)}
            onReset={() => updateFilter('counterparties', [])}
            placeholder="Select counterparties..."
            searchPlaceholder="Search counterparties..."
            hideLabel
          />
        );

      case 'currency':
        return (
          <MultiSelectFilter
            label="Currency"
            options={currencies}
            selectedValues={localInventoryFilters.currencies}
            onChange={(values) => updateFilter('currencies', values)}
            onReset={() => updateFilter('currencies', [])}
            placeholder="Select currencies..."
            searchPlaceholder="Search currencies..."
            hideLabel
          />
        );

      case 'exchange':
        return (
          <MultiSelectFilter
            label="Exchange"
            options={filterMetadata.exchanges ?? []}
            selectedValues={localInventoryFilters.exchanges}
            onChange={(values) => updateFilter('exchanges', values)}
            onReset={() => updateFilter('exchanges', [])}
            placeholder="Select exchanges..."
            searchPlaceholder="Search exchanges..."
            hideLabel
          />
        );

      case 'quantity':
        return renderConditionFilter('quantity', 'Quantity (Trade Date)', 'quantityCondition', quantityRange, 1);

      case 'settledQuantity':
        return renderConditionFilter(
          'settledQuantity',
          'Settled Quantity',
          'settledQuantityCondition',
          filterMetadata.settledQuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'caStatus':
        return (
          <MultiSelectFilter
            label="CA Status"
            options={caStatuses}
            selectedValues={localInventoryFilters.caStatuses}
            onChange={(values) => updateFilter('caStatuses', values)}
            onReset={() => updateFilter('caStatuses', [])}
            placeholder="Select CA statuses..."
            searchPlaceholder="Search CA statuses..."
            hideLabel
          />
        );

      case 'caType':
        return (
          <div>
            <MultiSelectFilter
              label="CA Type"
              options={filterMetadata.caTypes ?? []}
              selectedValues={localInventoryFilters.caTypes}
              onChange={(values) => updateFilter('caTypes', values)}
              onReset={() => updateFilter('caTypes', [])}
              placeholder="Select CA types..."
              searchPlaceholder="Search CA types..."
              hideLabel
            />
            <label className="flex items-center gap-1.5 mt-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filterDisplayOptions['caType']?.hideBlanks ?? false}
                onChange={(e) => updateFilterDisplayOption('caType', 'hideBlanks', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
              />
              Hide blanks
            </label>
          </div>
        );

      case 'brokerAccountName':
        return (
          <MultiSelectFilter
            label="Primary Account Name"
            options={filterMetadata.brokerAccountNames ?? []}
            selectedValues={localInventoryFilters.brokerAccountNames}
            onChange={(values) => updateFilter('brokerAccountNames', values)}
            onReset={() => updateFilter('brokerAccountNames', [])}
            placeholder="Select accounts..."
            searchPlaceholder="Search accounts..."
            hideLabel
          />
        );

      case 'brokerSettlementCounterparty':
        return (
          <MultiSelectFilter
            label="Primary Settlement CP"
            options={filterMetadata.brokerSettlementCounterparties ?? []}
            selectedValues={localInventoryFilters.brokerSettlementCounterparties}
            onChange={(values) => updateFilter('brokerSettlementCounterparties', values)}
            onReset={() => updateFilter('brokerSettlementCounterparties', [])}
            placeholder="Select counterparties..."
            searchPlaceholder="Search counterparties..."
            hideLabel
          />
        );

      case 'leverageRate':
        return renderConditionFilter('leverageRate', 'Leverage Rate', 'leverageRateCondition', leverageRateRange, 0.01);

      case 'valueEUR':
        return renderConditionFilter('valueEUR', 'Value, EUR', 'valueEURCondition', valueEURRange, 0.01);

      case 'benchmarkFee':
        return renderConditionFilter('benchmarkFee', 'Benchmark Fee (%)', 'benchmarkFeeCondition', benchmarkFeeRange, 0.01);

      case 'activeUtilisation':
        return renderConditionFilter('activeUtilisation', 'Active Utilisation (%)', 'activeUtilisationCondition', activeUtilisationRange, 0.01);

      case 'quantityOnLoan':
        return renderConditionFilter('quantityOnLoan', 'Quantity on Loan', 'quantityOnLoanCondition', quantityOnLoanRange, 1);

      case 'rebateRate':
        return renderConditionFilter('rebateRate', 'Rebate Rate (%)', 'rebateRateCondition', rebateRateRange, 0.01);

      case 'currencyBenchmark':
        return renderConditionFilter('currencyBenchmark', 'Currency Benchmark', 'currencyBenchmarkCondition', currencyBenchmarkRange, 0.01);

      case 'dailyPotential':
        return renderConditionFilter('dailyPotential', 'Daily Potential, EUR', 'dailyPotentialCondition', dailyPotentialRange, 0.01);

      case 'adtv30D':
        return renderConditionFilter('adtv30D', '30D ADTV', 'adtv30DCondition', adtv30DRange, 1);

      case 'adtvMultiple':
        return renderConditionFilter('adtvMultiple', 'ADTV Multiple', 'adtvMultipleCondition', adtvMultipleRange, 0.01);

      case 'adtvAdjusted':
        return renderConditionFilter('adtvAdjusted', 'ADTV-Adjusted, EUR', 'adtvAdjustedCondition', adtvAdjustedRange, 0.01);

      case 'quantityT1':
        return renderConditionFilter('quantityT1', 'Expected Settled Quantity T+1', 'quantityT1Condition', quantityT1Range, 1);

      case 'quantityT2':
        return renderConditionFilter('quantityT2', 'Expected Settled Quantity T+2', 'quantityT2Condition', quantityT2Range, 1);

      case 'quantityTx':
        return renderConditionFilter('quantityTx', 'Expected Settled Quantity T+X', 'quantityTxCondition', quantityTxRange, 1);

      case 'pendingTransfersOut':
        return renderConditionFilter(
          'pendingTransfersOut',
          'Pending Transfers (out)',
          'pendingTransfersOutCondition',
          filterMetadata.pendingTransfersOutRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'cimbEq2':
        return renderConditionFilter(
          'cimbEq2',
          'Quantity CIMB (EQ2)',
          'cimbEq2QuantityCondition',
          filterMetadata.cimbEq2QuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'cimbSf':
        return renderConditionFilter(
          'cimbSf',
          'Quantity CIMB (SF)',
          'cimbSfQuantityCondition',
          filterMetadata.cimbSfQuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'cimbSf3':
        return renderConditionFilter(
          'cimbSf3',
          'Quantity CIMB (SF3)',
          'cimbSf3QuantityCondition',
          filterMetadata.cimbSf3QuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'velocityL':
        return renderConditionFilter(
          'velocityL',
          'Quantity Velocity (L)',
          'velocityLQuantityCondition',
          filterMetadata.velocityLQuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'velocityM':
        return renderConditionFilter(
          'velocityM',
          'Quantity Velocity (M)',
          'velocityMQuantityCondition',
          filterMetadata.velocityMQuantityRange ?? { min: 0, max: 1000000 },
          1
        );

      case 'exDate':
        return (
          <div>
            {(localInventoryFilters.exDateFrom || localInventoryFilters.exDateTo) && (
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => {
                    setLocalInventoryFilters({
                      ...localInventoryFilters,
                      exDateFrom: null,
                      exDateTo: null,
                    });
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Reset
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  min={filterMetadata.exDateMin || undefined}
                  max={localInventoryFilters.exDateTo || filterMetadata.exDateMax || undefined}
                  value={localInventoryFilters.exDateFrom ?? ''}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    if (val && localInventoryFilters.exDateTo && val > localInventoryFilters.exDateTo) {
                      updateFilter('exDateFrom', localInventoryFilters.exDateTo);
                    } else {
                      updateFilter('exDateFrom', val);
                    }
                  }}
                  className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  min={localInventoryFilters.exDateFrom || filterMetadata.exDateMin || undefined}
                  max={filterMetadata.exDateMax || undefined}
                  value={localInventoryFilters.exDateTo ?? ''}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    if (val && localInventoryFilters.exDateFrom && val < localInventoryFilters.exDateFrom) {
                      updateFilter('exDateTo', localInventoryFilters.exDateFrom);
                    } else {
                      updateFilter('exDateTo', val);
                    }
                  }}
                  className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <label className="flex items-center gap-1.5 mt-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filterDisplayOptions['exDate']?.hideBlanks ?? false}
                onChange={(e) => updateFilterDisplayOption('exDate', 'hideBlanks', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
              />
              Hide blanks
            </label>
          </div>
        );

      case 'l2cpGroup':
        return (
          <div className="space-y-4">
            <MultiSelectFilter
              label="Counterparty"
              options={filterMetadata.l2cpCounterparties?.length ? filterMetadata.l2cpCounterparties : counterparties}
              selectedValues={localInventoryFilters.l2cpCounterparties}
              onChange={(values) => updateFilter('l2cpCounterparties', values)}
              onReset={() => updateFilter('l2cpCounterparties', [])}
              placeholder="Select counterparties..."
              searchPlaceholder="Search..."
              hideLabel
            />
            {renderConditionFilter(
              'l2cpQuantity',
              'Quantity',
              'l2cpQuantityCondition',
              filterMetadata.l2cpQuantityRange ?? { min: 0, max: 10000000 },
              1,
              false
            )}
            {renderConditionFilter(
              'l2cpAmountEur',
              'Amount (in EUR)',
              'l2cpAmountEurCondition',
              filterMetadata.l2cpAmountEurRange ?? { min: 0, max: 100000000 },
              1,
              false
            )}
            {renderConditionFilter(
              'l2cpRate',
              'Rate',
              'l2cpRateCondition',
              filterMetadata.l2cpRateRange ?? { min: -100, max: 100 },
              0.01,
              false
            )}
            {renderConditionFilter(
              'l2cpGrossRate',
              'Gross Rate',
              'l2cpGrossRateCondition',
              filterMetadata.l2cpGrossRateRange ?? { min: -100, max: 100 },
              0.01,
              false
            )}
            {renderConditionFilter(
              'l2cpSnpDelta',
              'SnP Delta',
              'l2cpSnpDeltaCondition',
              filterMetadata.l2cpSnpDeltaRange ?? { min: -100, max: 100 },
              0.01,
              false
            )}
          </div>
        );

      case 'export':
        return (
          <div>
            <button
              onClick={handleExport}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export as CSV
            </button>
            <p className="mt-2 text-xs text-gray-500 text-center">
              {filteredData.length} row{filteredData.length !== 1 ? 's' : ''} will be exported
            </p>
          </div>
        );

      default:
        return null;
    }
  }, [
    inventoryFilters, setInventoryFilters, statesData, isStatesLoading, isDataLoading, hasError,
    localInventoryFilters, setLocalInventoryFilters, updateFilter,
    accountIds, counterparties, currencies, leverageRateRange, valueEURRange, benchmarkFeeRange,
    activeUtilisationRange, quantityOnLoanRange, rebateRateRange, currencyBenchmarkRange,
    dailyPotentialRange, adtv30DRange, adtvMultipleRange, adtvAdjustedRange, handleExport, filteredData,
    filterMetadata,
    caStatuses, filterDisplayOptions,
    quantityRange, quantityT1Range, quantityT2Range, quantityTxRange,
    renderConditionFilter, updateFilterDisplayOption,
  ]);

  // Single counted source for the funnel badge + Clear All visibility; every
  // filter field must be represented inside countActiveInventoryFilters
  // (LR-518 r1 M-2 / r2 #4 — unit-tested there).
  const activeFiltersCount = countActiveInventoryFilters(localInventoryFilters);

  const hasActiveDisplayOptions = Object.values(filterDisplayOptions).some(
    (opt) => opt?.hideZero || opt?.hideBlanks
  );

  const hasActiveFilters = activeFiltersCount > 0 || hasActiveDisplayOptions;

  return (
    <div data-tour="inventory-filters" className={clsx(
      'fixed inset-y-0 right-0 z-[9998] flex flex-col transition-all duration-300',
      filterSidebarCollapsed ? 'w-18' : 'w-72'
    )}>
      <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 shadow-lg border-l border-gray-200">
        {/* Header: collapse button + preset zone */}
        {filterSidebarCollapsed ? (
          <div className="flex h-[60px] shrink-0 items-center">
            <button
              type="button"
              className="-m-1 p-1 text-gray-400 hover:text-gray-500 relative"
              onClick={toggleFilterSidebar}
            >
              <span className="sr-only">Toggle filter sidebar</span>
              <FunnelIcon className="h-[22px] w-[22px] stroke-[1.5]" aria-hidden="true" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                  {Math.min(activeFiltersCount, 6)}
                </span>
              )}
            </button>
          </div>
        ) : (
          <div
            className={clsx(
              'shrink-0 -mx-6 px-6 flex items-center bg-gray-50 border-b border-gray-200',
              presetIsDirty ? 'py-3' : 'h-[60px]'
            )}
          >
            <div className="flex items-center gap-6 w-full">
              <button
                type="button"
                className="-m-1 p-1 text-gray-400 hover:text-gray-500 flex-shrink-0"
                onClick={toggleFilterSidebar}
                aria-label="Collapse sidebar"
              >
                <ChevronRightIcon className="h-4 w-4 stroke-2" aria-hidden="true" />
              </button>
              <div className="flex-1 min-w-0" data-tour="inventory-saved-views">
                <SavedViewSelector
                  controller={inventorySavedViewsController}
                  hooks={getInventorySavedViewsHooks()}
                  currentUserEmail={currentUserEmail ?? null}
                  onActiveViewChange={onActiveViewChange}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tabs: Filters / Columns — below header */}
        {!filterSidebarCollapsed && (
          <div className="flex items-center gap-1 -mt-3 shrink-0">
            <button
              type="button"
              onClick={() => handleTabChange('filters')}
              className={clsx(
                'px-2.5 py-1 text-base font-semibold transition-colors',
                activeTab === 'filters'
                  ? 'text-gray-900 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Filters
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('columns')}
              className={clsx(
                'px-2.5 py-1 text-base font-semibold transition-colors',
                activeTab === 'columns'
                  ? 'text-gray-900 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Columns
            </button>
          </div>
        )}

        {/* Columns tab content */}
        {!filterSidebarCollapsed && activeTab === 'columns' && (
          <div className="flex flex-1 flex-col pb-4 -mt-2 min-h-0">
            <ColumnManagerPanel />
          </div>
        )}

        {/* Filters tab content */}
        {!filterSidebarCollapsed && activeTab === 'filters' && (
          <div className="space-y-6 pb-4">
            {hasActiveFilters && (
              <div className="flex justify-start -my-2 -ml-2.5 -mt-4">
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded transition-colors text-red-600 hover:text-red-800 uppercase"
                >
                  Clear All
                </button>
              </div>
            )}
            {(() => {
              const collapsibleFilters = DEFAULT_FILTER_ITEMS
                .filter((f: FilterItem) => f.type !== 'statement' && f.type !== 'export');
              const collapsibleIds = collapsibleFilters.map((f: FilterItem) => f.id);
              const allExpanded = collapsibleFilters.every(
                (f: FilterItem) => expandedFilterIds.includes(f.id) || getHasActiveValuesForFilter(f.id, f.type)
              );
              return (
                <div className="flex items-center justify-between -mt-2 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (allExpanded) {
                        collapseAllFilters();
                      } else {
                        expandAllFilters(collapsibleIds);
                      }
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              );
            })()}
            {sortFiltersByColumnOrder(DEFAULT_FILTER_ITEMS, columns).map((filter: FilterItem) => {
              const noCollapse = filter.type === 'statement' || filter.type === 'export';
              const hasActive = getHasActiveValuesForFilter(filter.id, filter.type);
              const expanded = expandedFilterIds.includes(filter.id) || hasActive;
              const columnId = FILTER_ID_TO_COLUMN_ID[filter.id] ?? null;
              const columnConfig = columnId ? columns.find((c: { id: string }) => c.id === columnId) : null;
              const isColumnVisible = columnConfig ? columnConfig.visible : true;
              if (noCollapse) {
                return (
                  <div key={filter.id} className="space-y-2">
                    {renderFilterComponent(filter.type)}
                  </div>
                );
              }
              return (
                <CollapsibleFilterSection
                  key={filter.id}
                  filterId={filter.id}
                  label={filter.label}
                  expanded={expanded}
                  onToggle={() => (expanded ? collapseFilter(filter.id) : expandFilter(filter.id))}
                  isFocused={focusedFilterId === filter.id}
                  hasActiveValues={hasActive}
                  columnId={columnId}
                  onLocateColumn={(colId) => setLocateColumnRequest({ columnId: colId, trigger: Date.now() })}
                  isColumnVisible={isColumnVisible}
                >
                  {renderFilterComponent(filter.type)}
                </CollapsibleFilterSection>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
};
